import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Mail, Phone, BookOpen, Calendar, Loader2, CheckCircle2, AlertCircle, MinusCircle, Clock, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
    acquis: { label: "Acquis", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
    en_cours: { label: "En cours", icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
    a_renforcer: { label: "À renforcer", icon: AlertCircle, color: "text-orange-600", bg: "bg-orange-50" },
    non_acquis: { label: "Non acquis", icon: MinusCircle, color: "text-destructive", bg: "bg-red-50" },
    non_evalue: { label: "Non évalué", icon: MinusCircle, color: "text-muted2", bg: "bg-surface" },
};

export default function FormateurDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("courses");

    useEffect(() => {
        if (!user) return;
        if (!["responsable", "admin"].includes(user.role)) { navigate("/dashboard"); return; }
        (async () => {
            try {
                const { data } = await api.get(`/responsable/formateurs/${id}`);
                setData(data);
            } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
                navigate("/formateurs");
            } finally { setLoading(false); }
        })();
    }, [id, user, navigate]);

    if (loading || !data) {
        return (
            <div className="min-h-screen bg-paper">
                <Navbar />
                <div className="max-w-4xl mx-auto px-6 py-16 text-center text-muted2">
                    <Loader2 size={16} className="animate-spin mx-auto mb-2" /> Chargement…
                </div>
            </div>
        );
    }

    const { formateur: f, courses, alternants_summary } = data;

    return (
        <div className="min-h-screen bg-paper">
            <Navbar />
            <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="formateur-detail-page">
                <Link to="/formateurs" className="inline-flex items-center gap-1.5 text-xs font-mono text-muted2 hover:text-ink mb-4" data-testid="formateur-back">
                    <ArrowLeft size={12} /> Retour à la liste
                </Link>

                {/* HEADER */}
                <div className="panel p-6 mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="flex-1 min-w-0">
                            <div className="label-eyebrow text-orange mb-1">Formateur</div>
                            <h1 className="h-display text-2xl sm:text-3xl font-semibold leading-tight" data-testid="formateur-name">
                                {f.prenom || ""} {f.nom || ""}
                            </h1>
                            <div className="flex flex-wrap gap-3 text-xs font-mono text-muted2 mt-2">
                                <span className="inline-flex items-center gap-1"><Mail size={11} /> {f.email}</span>
                                {f.phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {f.phone}</span>}
                                <span>Inscrit le {f.created_at ? new Date(f.created_at).toLocaleDateString("fr-FR") : "—"}</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-surface p-4 text-center min-w-[110px]">
                                <div className="label-eyebrow !text-[9px]">Cours / TPs</div>
                                <div className="h-display text-2xl font-bold mt-1 inline-flex items-center gap-1.5">
                                    <BookOpen size={16} className="text-orange" /> {courses.length}
                                </div>
                            </div>
                            <div className="bg-surface p-4 text-center min-w-[110px]">
                                <div className="label-eyebrow !text-[9px]">Alternants évalués</div>
                                <div className="h-display text-2xl font-bold mt-1 inline-flex items-center gap-1.5">
                                    <GraduationCap size={16} className="text-orange" /> {alternants_summary.length}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TABS */}
                <div className="flex gap-1 mb-4 border-b border-rule" data-testid="formateur-tabs">
                    <button
                        onClick={() => setTab("courses")}
                        className={`px-4 py-2.5 text-xs font-mono uppercase tracking-wider transition-colors ${tab === "courses" ? "border-b-2 border-orange text-ink -mb-px" : "text-muted2 hover:text-ink"}`}
                        data-testid="tab-courses"
                    >
                        Cours / TPs ({courses.length})
                    </button>
                    <button
                        onClick={() => setTab("alternants")}
                        className={`px-4 py-2.5 text-xs font-mono uppercase tracking-wider transition-colors ${tab === "alternants" ? "border-b-2 border-orange text-ink -mb-px" : "text-muted2 hover:text-ink"}`}
                        data-testid="tab-alternants"
                    >
                        Évaluations par alternant ({alternants_summary.length})
                    </button>
                </div>

                {/* COURSES TAB */}
                {tab === "courses" && (
                    courses.length === 0 ? (
                        <div className="panel p-10 text-center text-sm text-muted2" data-testid="courses-empty">
                            Ce formateur n&apos;a pas encore saisi de cours / TP.
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="courses-list">
                            {courses.map((c) => (
                                <div key={c.id} className="panel p-5" data-testid={`course-${c.id}`}>
                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="pill bg-ink text-white !text-[10px] uppercase">{c.kind === "tp" ? "TP" : "Cours"}</span>
                                                {c.niveau && <span className="pill bg-orange-50 text-orange-700 border border-orange-200 !text-[10px]">{c.niveau}</span>}
                                                {c.duration_hours && <span className="pill bg-surface !text-[10px]">{c.duration_hours}h</span>}
                                            </div>
                                            <h3 className="h-display text-lg font-semibold mt-2">{c.title}</h3>
                                            {c.description && <p className="text-sm text-ink/70 mt-1 leading-relaxed">{c.description}</p>}
                                        </div>
                                        <div className="text-xs font-mono text-muted2 inline-flex items-center gap-1 shrink-0">
                                            <Calendar size={11} /> {c.date ? new Date(c.date).toLocaleDateString("fr-FR") : "—"}
                                        </div>
                                    </div>

                                    {c.evaluations && c.evaluations.length > 0 && (
                                        <div className="mt-3 pt-3 border-t border-rule">
                                            <div className="label-eyebrow mb-2">Évaluations ({c.evaluations.length} alternant{c.evaluations.length > 1 ? "s" : ""})</div>
                                            <div className="space-y-2">
                                                {c.evaluations.map((ev) => (
                                                    <div key={ev.alternant_id || ev.alternant_email} className="bg-surface p-3 text-xs">
                                                        <div className="font-semibold text-sm text-ink mb-1.5">
                                                            {ev.alternant_prenom || ""} {ev.alternant_nom || ""}
                                                            {!ev.alternant_prenom && !ev.alternant_nom && ev.alternant_email}
                                                        </div>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {(ev.competencies || []).map((comp) => {
                                                                const meta = STATUS_META[comp.status] || STATUS_META.non_evalue;
                                                                const Icon = meta.icon;
                                                                return (
                                                                    <span key={comp.code} className={`inline-flex items-center gap-1 px-2 py-0.5 ${meta.bg} ${meta.color} text-[10px] font-mono`} title={comp.label || comp.code}>
                                                                        <Icon size={9} /> {comp.code} · {meta.label}
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )
                )}

                {/* ALTERNANTS TAB */}
                {tab === "alternants" && (
                    alternants_summary.length === 0 ? (
                        <div className="panel p-10 text-center text-sm text-muted2" data-testid="alternants-empty">
                            Aucun alternant n&apos;a encore été évalué par ce formateur.
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="alternants-list">
                            {alternants_summary.map((a) => {
                                const total = Object.values(a.by_status).reduce((s, n) => s + n, 0);
                                return (
                                    <div key={a.alternant_id} className="panel p-5" data-testid={`alt-summary-${a.alternant_id}`}>
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                                            <div>
                                                <h3 className="h-display text-lg font-semibold">
                                                    {a.alternant?.prenom || ""} {a.alternant?.nom || ""}
                                                    {!a.alternant?.prenom && !a.alternant?.nom && (a.alternant?.email || "—")}
                                                </h3>
                                                <div className="text-xs font-mono text-muted2">{a.alternant?.email}</div>
                                            </div>
                                            <div className="text-xs text-muted2">
                                                <span className="font-mono">{a.courses}</span> cours · <span className="font-mono">{total}</span> compétences évaluées
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                            {["acquis", "en_cours", "a_renforcer", "non_acquis", "non_evalue"].map((s) => {
                                                const meta = STATUS_META[s];
                                                const Icon = meta.icon;
                                                const n = a.by_status[s] || 0;
                                                return (
                                                    <div key={s} className={`p-3 ${meta.bg}`}>
                                                        <div className={`inline-flex items-center gap-1 ${meta.color} label-eyebrow`}>
                                                            <Icon size={11} /> {meta.label}
                                                        </div>
                                                        <div className={`h-display text-2xl font-bold mt-1 ${meta.color}`}>{n}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )
                )}
            </main>
        </div>
    );
}
